import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ComparisonTable from "../components/ComparisonTable";
import MadeInAmerica from "../components/MadeInAmerica";
import Button from "../components/Button";
import ContactModal from "../components/ContactModal";
import "../index.css"; 
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import SparkleIcon from "../assets/icons/Sparkle.svg";
import { fetchProductByTitle } from "../contentful";
import { CONTENT_TYPES } from "../constants/contentTypes";
import { toUrlFriendly, fromUrlFriendly } from "../utils/urlHelpers";

const ProductDetail = ({ products }) => {
  const { productTitle: urlProductTitle, type } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const isIndustrial = type === 'industrial';

  console.log('ProductDetail params:', { urlProductTitle, type, isIndustrial });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        if (!urlProductTitle) {
          console.error('No product title provided in URL');
          setNotFound(true);
          return;
        }

        if (!type) {
          console.error('No product type provided in URL');
          setNotFound(true);
          return;
        }

        setIsLoading(true);
        console.log('Fetching product with params:', { urlProductTitle, type, isIndustrial });
        const contentType = isIndustrial ? 'b2bCleaningProductData' : 'cleaningProductData';
        console.log('Using content type:', contentType);
        
        const data = await fetchProductByTitle(urlProductTitle, contentType);
        console.log('Received product data:', data);
        
        if (data) {
          console.log('Setting product data:', data.fields);
          setProduct(data);
          setNotFound(false);
        } else {
          console.log('No product data found');
          setNotFound(true);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [urlProductTitle, type, isIndustrial]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading product details...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-xl mb-4">Product not found</div>
        <Button
          onClick={() => navigate(-1)}
          variant="primary"
          size="medium"
        >
          Back to Products
        </Button>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const {
    productTitle = "",
    productHeroImage = {},
    price = 0,
    buyNowButtonUrl = "",
    shortProductDescription = "",
    keyFeatures = [],
    keyBenefits = [],
    productInUseImages = [],
    detailedProductDescription = null,
    frontLabelCallOuts = [],
    applications = "",
    howToUse = "",
    productMockupVariations = [],
    bottleSizing = "",
  } = product.fields || {};

  const imageUrl = productHeroImage?.fields?.file?.url || null;

  // Add rich text options for documentToReactComponents
  const richTextOptions = {
    renderNode: {
      paragraph: (node, children) => <p className="mb-4">{children}</p>,
      heading1: (node, children) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
      heading2: (node, children) => <h2 className="text-xl font-bold mb-3">{children}</h2>,
      heading3: (node, children) => <h3 className="text-lg font-bold mb-2">{children}</h3>,
      listItem: (node, children) => <li className="ml-4 mb-2">{children}</li>,
      orderedList: (node, children) => <ol className="list-decimal ml-6 mb-4">{children}</ol>,
      unorderedList: (node, children) => <ul className="list-disc ml-6 mb-4">{children}</ul>,
    },
  };

  // Helper function to safely render rich text
  const renderRichText = (content) => {
    if (!content || !content.content) return null;
    try {
      return documentToReactComponents(content, richTextOptions);
    } catch (error) {
      console.error('Error rendering rich text:', error);
      return null;
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative py-6 md:py-20 px-4 md:px-12 lg:px-24 overflow-hidden">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
            {/* Product Image - Spans 7 columns on desktop, 5 on mobile */}
            {imageUrl && (
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="order-1 col-span-5 md:col-span-7 flex items-center justify-center"
              >
                <img
                  src={imageUrl}
                  alt={productTitle || "Product Image"}
                  className="w-full h-auto object-contain rounded-3xl"
                />
              </motion.div>
            )}

            {/* Product Info - Spans 7 columns on desktop, 7 on mobile */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="order-2 col-span-7 md:col-span-5 flex flex-col justify-center bg-white p-4 md:p-8"
            >
              <h1 className="text-2xl md:text-5xl lg:text-6xl font-bold text-[#D3242A] mb-2 md:mb-6 uppercase" style={{ fontFamily: "Geogrotesque, sans-serif" }}>
                {productTitle}
              </h1>
              {shortProductDescription && (
                <p className="text-sm md:text-xl text-gray-700 mb-3 md:mb-8" style={{ fontFamily: "Montserrat, sans-serif" }}>
                  {shortProductDescription}
                </p>
              )}
              {price && !isIndustrial && (
                <p className="text-xl md:text-3xl font-bold text-[#D3242A] mb-3 md:mb-8" style={{ fontFamily: "Geogrotesque, sans-serif" }}>
                  ${price.toFixed(2)}
                </p>
              )}
              <div className="flex flex-col gap-2 md:gap-4">
                {isIndustrial ? (
                  <Button
                    variant="primary"
                    size="large"
                    onClick={() => setIsContactModalOpen(true)}
                  >
                    Request Quote
                  </Button>
                ) : (
                  <Button
                    href={buyNowButtonUrl === "Coming Soon!" ? undefined : buyNowButtonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary"
                    size="large"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (buyNowButtonUrl !== "Coming Soon!") {
                        window.open(buyNowButtonUrl, '_blank');
                      }
                    }}
                  >
                    {buyNowButtonUrl === "Coming Soon!" ? "Coming Soon!" : "Buy Now"}
                  </Button>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      {applications && (
        <section className="py-4 md:py-8 px-4 md:px-12 lg:px-24">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4">
              <img 
                src={SparkleIcon} 
                alt="Sparkle Icon" 
                className="w-8 h-8 md:w-12 md:h-12 transform scale-y-[-1]"
                style={{ filter: 'invert(19%) sepia(95%) saturate(3836%) hue-rotate(346deg) brightness(87%) contrast(96%)' }}
              />
              <h2 className="text-2xl md:text-3xl font-extrabold text-[#D3242A] uppercase" style={{ fontFamily: 'Geogrotesque, sans-serif' }}>
                Applications
              </h2>
            </div>
            {Array.isArray(applications) ? (
              <ul className="list-disc ml-6 md:ml-8 text-base md:text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {applications.map((item, idx) => (
                  <li key={idx} className="mb-1 md:mb-2">{item}</li>
                ))}
              </ul>
            ) : (
              <div className="text-base md:text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {applications.split('\n').map((paragraph, idx) => {
                  if (paragraph.includes('•')) {
                    const items = paragraph.split('•').filter(item => item.trim());
                    return (
                      <ul key={idx} className="list-disc ml-6 md:ml-8 mb-1 md:mb-2">
                        {items.map((item, itemIdx) => (
                          <li key={itemIdx} className="mb-1 md:mb-2">{item.trim()}</li>
                        ))}
                      </ul>
                    );
                  }
                  return <p key={idx} className="mb-2 md:mb-4">{paragraph}</p>;
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {keyBenefits?.length > 0 && (
        <section className="py-4 md:py-8 px-4 md:px-12 lg:px-24">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4">
              <img 
                src={SparkleIcon} 
                alt="Sparkle Icon" 
                className="w-8 h-8 md:w-12 md:h-12 transform scale-y-[-1]"
                style={{ filter: 'invert(19%) sepia(95%) saturate(3836%) hue-rotate(346deg) brightness(87%) contrast(96%)' }}
              />
              <h2 className="text-2xl md:text-3xl font-extrabold text-[#D3242A] uppercase" style={{ fontFamily: 'Geogrotesque, sans-serif' }}>
                Benefits
              </h2>
            </div>
            <ul className="list-disc ml-6 md:ml-8 text-base md:text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {keyBenefits.map((benefit, idx) => (
                <li key={idx} className="mb-1 md:mb-2">{benefit}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* How To Use Section */}
      {howToUse && (
        <section className="py-4 md:py-8 px-4 md:px-12 lg:px-24">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4">
              <img 
                src={SparkleIcon} 
                alt="Sparkle Icon" 
                className="w-8 h-8 md:w-12 md:h-12 transform scale-y-[-1]"
                style={{ filter: 'invert(19%) sepia(95%) saturate(3836%) hue-rotate(346deg) brightness(87%) contrast(96%)' }}
              />
              <h2 className="text-2xl md:text-3xl font-extrabold text-[#D3242A] uppercase" style={{ fontFamily: 'Geogrotesque, sans-serif' }}>
                How To Use
              </h2>
            </div>
            {howToUse.trim().split('\n').some(line => line.trim().startsWith('-')) ? (
              <ul className="list-disc ml-6 md:ml-8 text-base md:text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {howToUse.trim().split('\n').filter(line => line.trim().startsWith('-')).map((line, idx) => (
                  <li key={idx} className="mb-1 md:mb-2">{line.replace(/^\s*-\s*/, '')}</li>
                ))}
              </ul>
            ) : (
              <div className="text-base md:text-lg whitespace-pre-wrap" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {howToUse}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Sizing Section */}
      {bottleSizing && (
        <section className="py-4 md:py-8 px-4 md:px-12 lg:px-24">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4">
              <img 
                src={SparkleIcon} 
                alt="Sparkle Icon" 
                className="w-8 h-8 md:w-12 md:h-12 transform scale-y-[-1]"
                style={{ filter: 'invert(19%) sepia(95%) saturate(3836%) hue-rotate(346deg) brightness(87%) contrast(96%)' }}
              />
              <h2 className="text-2xl md:text-3xl font-extrabold text-[#D3242A] uppercase" style={{ fontFamily: 'Geogrotesque, sans-serif' }}>
                Sizing
              </h2>
            </div>
            <ul className="list-disc ml-6 md:ml-8 text-base md:text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {bottleSizing.split('+').map((size, idx) => (
                <li key={idx} className="mb-1 md:mb-2">{size.trim()}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Made in America Section */}
      <section className="py-8 md:py-16 px-4 md:px-12 lg:px-24">
        <div className="max-w-[1600px] mx-auto">
          <MadeInAmerica />
        </div>
      </section>

      {/* Comparison Table - Only show for household products */}
      {!isIndustrial && (
        <section className="py-16">
          <div className="max-w-[1600px] mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              <ComparisonTable />
            </motion.div>
          </div>
        </section>
      )}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </div>
  );
};

export default ProductDetail;
